import { useInventoryAnalytics } from '@hooks/useAnalytics'

export function InventoryAnalyticsPage() {
  const { data, isLoading, error } = useInventoryAnalytics()

  const analytics = data?.data

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Inventory Analytics</h1>
        <p className="text-gray-600 text-sm mt-1">
          Track bulk stock, packed goods, and repacking progress
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading inventory data...</div>
      ) : error ? (
        <div className="alert alert-error">
          Failed to load inventory analytics.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-blue-500">
              <div className="text-sm text-blue-600 font-semibold uppercase">
                Active Repacking Jobs
              </div>
              <div className="text-3xl font-bold text-blue-900 mt-2">
                {analytics?.activeRepackingJobs || 0}
              </div>
              <div className="text-xs text-blue-700 mt-1">
                In progress
              </div>
            </div>

            <div className="card bg-gradient-to-br from-green-50 to-green-100 border-l-4 border-green-500">
              <div className="text-sm text-green-600 font-semibold uppercase">
                Packed Goods Available
              </div>
              <div className="text-3xl font-bold text-green-900 mt-2">
                {(analytics?.packedGoodsCount || 0).toLocaleString()}
              </div>
              <div className="text-xs text-green-700 mt-1">
                Units ready for sale
              </div>
            </div>

            <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-l-4 border-purple-500">
              <div className="text-sm text-purple-600 font-semibold uppercase">
                Bulk Batches
              </div>
              <div className="text-3xl font-bold text-purple-900 mt-2">
                {analytics?.bulkInventory?.length || 0}
              </div>
              <div className="text-xs text-purple-700 mt-1">
                Waiting for repacking
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Bulk Stock by Product</h2>
              </div>

              {analytics?.bulkInventory && analytics.bulkInventory.length > 0 ? (
                <div className="space-y-4">
                  {analytics.bulkInventory.map((item: any) => (
                    <div key={item.product.id} className="border-b pb-4 last:border-b-0">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.product.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.batchCount} batches
                          </p>
                        </div>
                        <p className="font-bold text-gray-900">
                          {item.totalQty.toFixed(2)} {item.product.baseUom}
                        </p>
                      </div>
                      <div className="w-full bg-gray-200 rounded h-2">
                        <div className="bg-blue-500 h-2 rounded" style={{ width: '75%' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No bulk inventory</p>
              )}
            </div>

            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Repacking Progress</h2>
              </div>

              {analytics?.repackingProgress && analytics.repackingProgress.length > 0 ? (
                <div className="space-y-4">
                  {analytics.repackingProgress.slice(0, 5).map((job: any) => {
                    const progress =
                      (job.actualOutput / job.expectedYield) * 100
                    return (
                      <div key={job.id} className="border-b pb-4 last:border-b-0">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-gray-900">
                              {job.bulkBatch.product.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {job.repackerUser.fullName}
                            </p>
                          </div>
                          <p className="font-bold text-orange-600">
                            {progress.toFixed(0)}%
                          </p>
                        </div>
                        <div className="w-full bg-gray-200 rounded h-2">
                          <div
                            className="bg-orange-500 h-2 rounded transition-all"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          {job.actualOutput.toFixed(2)} / {job.expectedYield.toFixed(2)} {job.bulkBatch.product.baseUom}
                        </p>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No active repacking jobs</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
